import { useState, useMemo, useCallback } from "react";
import {
    Combobox,
    InputBase,
    useCombobox,
    Group,
    Stack,
    Box,
    Text,
    Chip,
    ScrollArea,
    CloseButton,
    Input,
    UnstyledButton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertTriangle } from "../../../icons";
import styles from "./ConversionInput.module.css";

export const ConversionInput = ({
    label,
    error,
    required,
    value,
    onChange,
    onBlur,
    conversion,
    featureName,
    description,
    showLabel = true,
    additionalProps = {},
    metricsPosition = "beside",
    metricsVariant = "chip"
}) => {
    const isMobile = useMediaQuery("(max-width: 48em)");
    const [search, setSearch] = useState("");

    const defaultMetric = useMemo(() => {
        const fromProps = additionalProps?.DefaultMetric;
        if (fromProps && conversion?.metrics?.includes(fromProps)) {
            return fromProps;
        }
        return conversion?.metrics?.[0] || "cm";
    }, [additionalProps?.DefaultMetric, conversion?.metrics]);

    const [selectedMetric, setSelectedMetric] = useState(defaultMetric);
    const metrics = conversion?.metrics || [];
    // Narrow screens always stack, whatever the configured position: side by side
    // there is not enough room for the field and the metrics.
    const stackMetricsBelowField = (isMobile || metricsPosition === "below") && metrics.length > 0;

    const options = useMemo(() => {
        if (!conversion?.options?.[selectedMetric]) return [];

        let filteredOptions = conversion.options[selectedMetric];
        const startKey = additionalProps?.StartKey;
        const endKey = additionalProps?.EndKey;

        if (startKey !== undefined || endKey !== undefined) {
            filteredOptions = filteredOptions.filter((opt) => {
                const keyValue = parseInt(opt.value, 10);
                if (Number.isNaN(keyValue)) return true;

                let valid = true;
                if (startKey !== undefined) {
                    valid = valid && keyValue >= startKey;
                }
                if (endKey !== undefined) {
                    valid = valid && keyValue <= endKey;
                }
                return valid;
            });
        }

        const mappedOptions = filteredOptions.map((opt) => ({
            value: String(opt.value),
            label: opt.label,
        }));

        const seenValues = new Set();
        return mappedOptions.filter((opt) => {
            if (seenValues.has(opt.value)) {
                return false;
            }
            seenValues.add(opt.value);
            return true;
        });
    }, [conversion?.options, selectedMetric, additionalProps]);

    const combobox = useCombobox({
        onDropdownClose: () => {
            combobox.resetSelectedOption();
            setSearch("");
            onBlur?.();
        },
        onDropdownOpen: () => {
            combobox.focusSearchInput();
        },
    });

    const handleMetricChange = useCallback(
        (newMetric) => {
            setSelectedMetric(newMetric);
            setSearch("");
            combobox.resetSelectedOption();
        },
        [combobox]
    );

    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter((opt) => opt.label && opt.label.toLowerCase().includes(q));
    }, [options, search]);

    const hasValue = value !== undefined && value !== null && value !== "";
    const normalizedValue = hasValue ? String(value) : null;
    const selectedLabel = useMemo(() => {
        const opt = options.find((o) => o.value === normalizedValue);
        return opt?.label ?? (normalizedValue ?? null);
    }, [options, normalizedValue]);

    const handleOptionSubmit = useCallback(
        (val) => {
            onChange(val);
            combobox.closeDropdown();
        },
        [onChange, combobox]
    );

    const handleClear = useCallback(
        (e) => {
            e.stopPropagation();
            onChange("");
        },
        [onChange]
    );

    const inputBaseStyles = stackMetricsBelowField
        ? {
              root: { width: "100%" },
              wrapper: { width: "100%" },
              input: { width: "100%", minWidth: 0 },
          }
        : {
              root: { width: "auto", flex: "0 0 auto" },
              wrapper: { width: "auto" },
              input: { width: "auto", minWidth: "10rem" },
          };

    const wrapperStyle = stackMetricsBelowField ? { width: "100%" } : { width: "auto" };

    const comboboxOptions = filteredOptions.map((opt) => (
        <Combobox.Option value={opt.value} key={opt.value} active={opt.value === normalizedValue}>
            <span className={styles.optionLabel}>{opt.label}</span>
        </Combobox.Option>
    ));

    const valueCombobox = (
        <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} withinPortal>
            <Combobox.Target>
                <InputBase
                    component="button"
                    type="button"
                    pointer
                    rightSection={
                        error ? (
                            <IconAlertTriangle size={18} stroke={1.5} className="input-error-icon" />
                        ) : hasValue ? (
                            <CloseButton size="sm" onMouseDown={handleClear} aria-label="Clear selection" />
                        ) : (
                            <Combobox.Chevron />
                        )
                    }
                    rightSectionPointerEvents={hasValue && !error ? "all" : "none"}
                    onClick={() => combobox.toggleDropdown()}
                    classNames={{
                        root: styles.root,
                    }}
                    styles={inputBaseStyles}
                >
                    {selectedLabel ? (
                        <span className={styles.selectedValue}>{selectedLabel}</span>
                    ) : (
                        <span className={styles.placeholder}>Select a value</span>
                    )}
                </InputBase>
            </Combobox.Target>

            <Combobox.Dropdown className={styles.dropdown}>
                <Combobox.Search
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    placeholder="Search..."
                />
                <ScrollArea.Autosize mah={260} type="scroll">
                    <Combobox.Options>
                        {comboboxOptions.length > 0 ? (
                            comboboxOptions
                        ) : (
                            <Combobox.Empty>No options found</Combobox.Empty>
                        )}
                    </Combobox.Options>
                </ScrollArea.Autosize>
            </Combobox.Dropdown>
        </Combobox>
    );

    const valueSelect = (
        <Input.Wrapper
            error={error}
            required={required}
            withAsterisk={!showLabel && required}
            style={wrapperStyle}
        >
            {valueCombobox}
        </Input.Wrapper>
    );

    const metricGroupProps = {
        gap: metricsVariant === "text" ? "xs" : 4,
        mt: 0,
        align: "center",
        wrap: stackMetricsBelowField ? "wrap" : "nowrap",
        // Below the field the metrics sit on their own row, aligned to its right edge.
        justify: stackMetricsBelowField ? "flex-end" : undefined,
        className: stackMetricsBelowField ? styles.metricGroupMobile : undefined,
    };

    const metricSelector = metricsVariant === "text" ? (
        <Group {...metricGroupProps}>
            {metrics.map((metric) => (
                <UnstyledButton
                    key={metric}
                    onClick={() => handleMetricChange(metric)}
                    className={styles.metricText}
                    data-selected={metric === selectedMetric || undefined}
                >
                    {metric.toUpperCase()}
                </UnstyledButton>
            ))}
        </Group>
    ) : (
        <Chip.Group value={selectedMetric} onChange={handleMetricChange}>
            <Group {...metricGroupProps}>
                {metrics.map((metric) => (
                    <Chip key={metric} value={metric} size="xs">
                        {metric.toUpperCase()}
                    </Chip>
                ))}
            </Group>
        </Chip.Group>
    );

    if (!conversion || metrics.length === 0) {
        return (
            <Text c="red" size="sm">
                Configuration error: No conversion data available for {label}
            </Text>
        );
    }

    return (
        <Box
            className={`${styles.container} ${styles.containerCompact} ${stackMetricsBelowField ? styles.containerCompactMobile : ""}`}
            data-ui="conversion-input"
        >
            <Stack gap="xs">
                {label && (
                    <Text size="sm" fw={500}>
                        {label}
                    </Text>
                )}
                {stackMetricsBelowField ? (
                    <Stack gap="xs" className={styles.fieldStackMobile}>
                        {valueSelect}
                        {metricSelector}
                    </Stack>
                ) : (
                    <Group gap="xs" align="center" wrap="nowrap">
                        {valueSelect}
                        {metricSelector}
                    </Group>
                )}

                {description && (
                    <Text size="xs" c="dimmed">
                        {description}
                    </Text>
                )}
            </Stack>
        </Box>
    );
};
